// pms-core / domain — PROPOSING a year's periods from the school-holiday zones.
//
// Step 5 of PMS-SEASON-CALENDAR-BRIEF. Re-cutting next year's calendar should be
// entering about five date ranges, not retyping eight periods.
//
// ── A PROPOSAL, and the distinction is the whole design ────────────────────
//
// This computes a starting skeleton. The operator edits it, and the EDITED
// result is what is stored — the calendar is authored data thereafter and is
// never silently recomputed. Très Haute is the proof that it must work this
// way: « Noël and August are the peak » is a commercial judgement about this
// hotel, not a fact derivable from a zone table. Anything this function claims
// to know about demand is a default someone can overrule, and the ones that
// matter are parameters rather than constants.
//
// ── Zone A/B only ──────────────────────────────────────────────────────────
//
// « Mainly A and B; C/Paris barely moves the needle » (operator, 2026-08-10).
// The hotel is academically in zone C itself, which is exactly why the zone
// that matters is where the guests come from rather than where the hotel is.
// This function takes whatever ranges it is given and does not know about
// zones at all — the selection happens before it, and `zones` is carried
// through only as documentation of why a period exists.

import type { Cents, IsoDate } from './types.js';
import type { RateSeason } from './rateParameters.js';
import { datesBetween } from './rateGenerator.js';
import { fiscalYearRange, type CalendarPeriod } from './gridGenerator.js';

export interface DateRange {
  from: IsoDate;
  to: IsoDate;
}

/** One school-holiday stretch for the zones that matter to this hotel. */
export interface ZoneHoliday extends DateRange {
  /** « Vacances de Noël / Nouvel An » — becomes the period's description, so it
   *  reads like the sheet the operator already knows. */
  label: string;
  /** « A/B », « A ». Carried onto the proposed period as documentation. */
  zones: string;
}

/** A period before it has been stored — identical to a `CalendarPeriod` minus
 *  the identity, which only the database can give it. */
export type ProposedPeriod = Omit<CalendarPeriod, 'id'>;

export interface ProposePeriodsInput {
  /** Fiscal year, named by its end: 2026 = 2025-12-01 → 2026-11-30. */
  year: number;
  holidays: ZoneHoliday[];
  /** Stretches this hotel treats as peak. Defaults to `defaultPeaks` — the
   *  Christmas holiday and August — which is a judgement, not a derivation. */
  peaks?: DateRange[];
  /**
   * Starting base per tier, if the operator has one in mind.
   *
   * OPTIONAL, and the design depends on it being so: the base is set against
   * competing hotels and re-adjusted all year (brief §9), so it is not
   * something this function could compute and not something it may block on.
   * Omitted, every proposed period comes out at 0 — visibly unset rather than
   * confidently wrong.
   */
  basesBySeason?: Record<RateSeason, Cents>;
}

/**
 * The two stretches this hotel prices as peak.
 *
 * The Christmas one is FOUND among the holidays rather than hard-coded to a
 * date: school holidays move by up to a week each year, and a fixed
 * « 20 December » would silently mis-cut the most valuable fortnight of the
 * season. August is a month and does move.
 */
export function defaultPeaks(year: number, holidays: ZoneHoliday[]): DateRange[] {
  const christmas = `${year - 1}-12-25`;
  const noel = holidays.find((h) => christmas >= h.from && christmas <= h.to);
  return [
    ...(noel ? [{ from: noel.from, to: noel.to }] : []),
    { from: `${year}-08-01`, to: `${year}-08-31` },
  ];
}

/** Months priced as MOYENNE when no holiday applies — deep winter and autumn.
 *  Everything else off-holiday is BASSE: the hotel reopens slowly in December
 *  and again in May, and the shoulder is not worth a middle price. */
const MOYENNE_MONTHS = new Set([1, 2, 9, 10]);

const inAny = (ranges: DateRange[], date: IsoDate): boolean =>
  ranges.some((r) => date >= r.from && date <= r.to);

/**
 * The tier a single date proposes, before coalescing.
 *
 * Order matters: peak beats holiday beats month. A peak stretch is usually
 * inside a holiday, and the whole point is that it outranks it.
 */
function tierOn(date: IsoDate, holidays: ZoneHoliday[], peaks: DateRange[]): RateSeason {
  if (inAny(peaks, date)) return 'tresHaute';
  if (inAny(holidays, date)) return 'haute';
  const month = Number(date.slice(5, 7));
  return MOYENNE_MONTHS.has(month) ? 'moyenne' : 'basse';
}

/**
 * Propose the year's periods.
 *
 * ── Why per-DAY then coalesce, rather than splitting at boundaries ─────────
 *
 * Splitting the year at holiday edges looks cheaper and is wrong at exactly the
 * awkward places: the off-holiday tier depends on the MONTH, so a segment
 * running 7 February → 2 March would need splitting again, and a peak sitting
 * inside a holiday needs a third cut. Classifying each day and then merging
 * equal neighbours gets every boundary right without enumerating them — and the
 * boundaries are precisely where a year's pricing is easiest to get subtly
 * wrong.
 */
export function proposePeriods(input: ProposePeriodsInput): ProposedPeriod[] {
  const { year, holidays, basesBySeason } = input;
  const peaks = input.peaks ?? defaultPeaks(year, holidays);
  const { from, to } = fiscalYearRange(year);

  const out: ProposedPeriod[] = [];
  let current: ProposedPeriod | null = null;

  for (const date of datesBetween(from, to)) {
    const season = tierOn(date, holidays, peaks);
    const holiday = holidays.find((h) => date >= h.from && date <= h.to);

    if (current !== null && current.season === season) {
      current.to = date;
      continue;
    }
    current = {
      from: date,
      to: date,
      description: describe(season, holiday),
      zones: holiday?.zones ?? null,
      season,
      baseCents: basesBySeason?.[season] ?? 0,
    };
    out.push(current);
  }

  return out;
}

/** A description that reads like the sheet — the holiday's own name when there
 *  is one, and a plain statement of the tier when there is not. */
function describe(season: RateSeason, holiday: ZoneHoliday | undefined): string {
  if (holiday) return holiday.label;
  switch (season) {
    case 'tresHaute':
      return 'Haute saison touristique';
    case 'haute':
      return 'Vacances scolaires';
    case 'moyenne':
      return 'Hors vacances — moyenne saison';
    case 'basse':
      return 'Hors vacances — basse saison';
  }
}

/**
 * How a proposal differs from a calendar that already exists — the readable
 * answer to « what would re-deriving change? ».
 *
 * Exists so that re-running the proposal against an authored year is a SAFE
 * thing to do: the operator sees the difference and decides, rather than
 * choosing between overwriting their edits and never checking.
 */
export interface ProposalDiff {
  /** Proposed stretches with no exactly-matching period in the calendar. */
  added: ProposedPeriod[];
  /** Existing periods the proposal does not reproduce. */
  removed: CalendarPeriod[];
  /** Same dates, different tier. */
  retiered: { from: IsoDate; to: IsoDate; was: RateSeason; proposed: RateSeason }[];
}

export function diffProposal(
  existing: CalendarPeriod[],
  proposed: ProposedPeriod[],
): ProposalDiff {
  const key = (p: { from: IsoDate; to: IsoDate }) => `${p.from}|${p.to}`;
  const byRange = new Map(existing.map((p) => [key(p), p] as const));
  const proposedByRange = new Map(proposed.map((p) => [key(p), p] as const));

  const added: ProposedPeriod[] = [];
  const retiered: ProposalDiff['retiered'] = [];

  for (const p of proposed) {
    const match = byRange.get(key(p));
    if (match === undefined) {
      added.push(p);
    } else if (match.season !== p.season) {
      retiered.push({ from: p.from, to: p.to, was: match.season, proposed: p.season });
    }
  }

  const removed = existing.filter((p) => !proposedByRange.has(key(p)));
  return { added, removed, retiered };
}
